// src/components/chat/MessageBubble.tsx
import * as React from "react";
import { MessageBubbleProps } from "./types/ChatUITypes";
import { useChatSettings } from "../../contexts/ChatSettingsContext"; // NEW: Access to settings
import { useSession } from "../../contexts/SessionContext"; // NEW: For updating message state
import MarkdownRenderer from "../../helpers/MarkdownRenderer";
import CopyButton from "./CopyButton";
import FactCheckButton from "./FactCheckButton";
import FeedbackButtons from "./feedback/FeedbackButtons"; // NEW: Import feedback buttons
import SelectedTextIndicator from "./SelectedTextIndicator";
import ThinkingSection from "./ThinkingSection"; // NEW: Import our thinking component
import ToolProcessingSection from "./ToolProcessingSection"; // NEW: Tool processing indicator
import WebToolsSection from "./WebToolsSection"; // NEW: Unified web tools component
import { EditPreviewList } from "../autoRedline/EditPreviewList"; // NEW: Import auto-redline component
import { AddCommentCard } from "../addComment/AddCommentCard"; // NEW: Import add-comment card component
import { WebSearchEvent } from "../../interfaces/webSearchTypes"; // NEW: For web search events
import { WebToolEvent, WebSearchToolEvent, WebFetchToolEvent } from "../../interfaces/webToolTypes"; // For inline web tools rendering
import { AutoRedlineOutput } from "../../interfaces/autoRedlineTypes"; // NEW: Import auto-redline types
import { DynamicForm } from "../DynamicForm"; // NEW: Import workflow form component
import { FormRequestPayload } from "../../interfaces/formTypes"; // NEW: Import form types
import { PlaybookViewer } from "../PlaybookViewer"; // NEW: Import playbook viewer component
import { PlaybookSelectCard } from "../PlaybookSelectCard"; // NEW: Import playbook selection card
import { NormalizedPlaybookResult } from "../../interfaces/playbookTypes"; // NEW: Import playbook types
import { CatylexSearchSection, CatylexProcessingSection } from "../CatylexSearch"; // NEW: Import Catylex search components
import { SharePointSearchSection, SharePointProcessingSection } from "../SharePointSearch"; // NEW: Import SharePoint search components
import {
  OutlookMailSection,
  OutlookMailProcessing,
  BEAUTIFY_EMAIL_PREFIX,
  BeautifyIndicator,
} from "../OutlookMail"; // NEW: Import Outlook mail components
import { QuickAnalysisIndicator } from "./QuickStartButtons";
import {
  DraftCertificateCard,
  DraftCertificateIndicator,
} from "./draftCertificate/DraftCertificateAction";
import ExportToWordButton from "./draftCertificate/ExportToWordButton";
import { InlineToolEvent, StaleApplyAttempt } from "../../interfaces/chatSession"; // NEW: Import inline tool event type
import { CommentApplyStatus } from "../../interfaces/addCommentTypes"; // NEW: Import add-comment status type
import { OutlookMailResult } from "../../interfaces/outlookMailTypes";
import { useAgent } from "../../contexts/AgentContext"; // For agent ID
import { createLogger } from "../../utils/logger";
import { track } from "../../utils/analytics";
import PromptCoachPanel from "./PromptCoachPanel"; // NEW: Prompt Coach component
import { extractPromptCoach } from "../../utils/extractPromptCoach"; // NEW: Extraction helper
import { parsePromptCoach } from "../../utils/promptCoachParser"; // NEW: Parser for validation
import { DOUBLE_CHECK_SUFFIX } from "../../config/constants";
import { FEATURE_FLAGS } from "../../config/featureFlags";
import { ADD_COMMENT_PROCESSING_MESSAGES } from "../../config/toolProcessingMessages"; // NEW: Add-comment processing messages
import { WebSearchResultsProvider } from "../../contexts/WebSearchResultsContext";
import { DocumentCitationProvider } from "../../contexts/DocumentCitationContext";
import { DocumentCitation } from "../../interfaces/documentCitationTypes";

// Create a logger specifically for the MessageBubble component
const log = createLogger("MessageBubble");

// Stable empty object to avoid re-creating DocumentCitationProvider context on every render
const EMPTY_CITATIONS: Record<string, DocumentCitation> = {};

/**
 * Strip incomplete cite and doc-cite tags from content during streaming.
 * Prevents raw tag text (e.g., '<cite source="X" url="https://...' or '<doc-cite ref="0')
 * from appearing while the LLM is still outputting the citation.
 *
 * Scans through content to find ALL cite/doc-cite tags and strips from the first
 * incomplete one to end. This handles incomplete tags anywhere in content,
 * not just at the end (important when content is split for tool outputs).
 */
function stripIncompleteCiteTags(content: string): string {
  if (!content) return content;

  // Check both <cite and <doc-cite tags
  const tagPrefixes = ["<cite", "<doc-cite"];

  let earliestIncomplete = -1;

  for (const prefix of tagPrefixes) {
    let searchStart = 0;

    while (true) {
      const tagIndex = content.indexOf(prefix, searchStart);
      if (tagIndex === -1) break;

      // Make sure we match the exact prefix (e.g., <cite should not match <doc-cite)
      if (prefix === "<cite" && content.substring(tagIndex).startsWith("<doc-cite")) {
        searchStart = tagIndex + 1;
        continue;
      }

      const afterTag = content.substring(tagIndex);
      const closeIndex = afterTag.indexOf("/>");

      if (closeIndex === -1) {
        // Incomplete tag found
        if (earliestIncomplete === -1 || tagIndex < earliestIncomplete) {
          earliestIncomplete = tagIndex;
        }
        break;
      }

      // Complete tag found - continue searching after it
      searchStart = tagIndex + closeIndex + 2;
    }
  }

  if (earliestIncomplete !== -1) {
    return content.substring(0, earliestIncomplete);
  }

  return content;
}

/**
 * Enhanced MessageBubbleProps interface to support thinking content
 * This extends the existing interface while maintaining full backward compatibility
 */
interface EnhancedMessageBubbleProps extends MessageBubbleProps {
  // NEW: Optional web search state and events
  webSearchEvents?: any[]; // Using any[] temporarily to match ChatMessage
  isSearching?: boolean;

  // NEW: Optional web fetch state and events
  webFetchEvents?: any[]; // Using any[] temporarily to match ChatMessage
  isFetching?: boolean;

  // NEW: Optional auto-redline outputs
  autoRedlineOutputs?: AutoRedlineOutput[];

  // NEW: Optional workflow form payload
  formRequestPayload?: FormRequestPayload;

  // NEW: Callback when form is submitted
  onFormSubmitted?: (messageId: string) => void;

  // NEW: Optional playbook result
  playbookResult?: NormalizedPlaybookResult;

  // NEW: Optional tool processing state
  isProcessingTool?: boolean;
}

const MessageBubble: React.FC<EnhancedMessageBubbleProps> = ({
  message,
  isStreaming = false,
  selectedText = "",
  // NEW: Web search-related props with sensible defaults
  webSearchEvents = [],
  isSearching = false,
  // NEW: Web fetch-related props with sensible defaults
  webFetchEvents = [],
  isFetching = false,
  // NEW: Auto-redline props
  autoRedlineOutputs = [],
  // NEW: Composer mode state from message creation time
  composerModeActiveOnCreation = false,
  // NEW: Prompt Coach click handler
  onPromptClick,
  // NEW: Fact-check click handler
  onFactCheck,
  // NEW: Workflow form props
  formRequestPayload,
  onFormSubmitted,
  // NEW: Playbook result
  playbookResult,
  // NEW: Playbook selection handler
  onPlaybookSelected,
  onDraftCertificate,
  isDraftCertificateAnswer = false,
  // NEW: Tool processing props with sensible defaults
  isProcessingTool = false,
}) => {
  const isUser = message.role === "user";

  // NEW: Access chat settings to determine if thinking should be displayed
  const { settings, isExtendedThinkingEnabled, isComposerModeEnabled } = useChatSettings();

  // Get agent context for feedback
  const { currentAgent } = useAgent();

  // Get session context for updating message state
  const { updateMessageForAgent } = useSession();

  // Generate a stable session ID for feedback tracking
  // Uses message ID as the stable identifier to prevent new sessions on re-renders
  const feedbackSessionId = React.useMemo(() => {
    return `${currentAgent?.id || "unknown"}-session-${message.id}`;
  }, [currentAgent?.id, message.id]);

  // Handler for direct outlook mail send/draft completion
  // Updates the InlineToolEvent from preview → success so the card re-renders
  const handleOutlookMailSendComplete = React.useCallback(
    (eventId: string, successResult: OutlookMailResult) => {
      const agentId = currentAgent?.id || "unknown";
      const currentEvents = message.inlineToolEvents || [];

      // Replace the preview event's outlookMailResult with the success result
      const updatedEvents = currentEvents.map((evt: InlineToolEvent) => {
        if (evt.id === eventId) {
          return { ...evt, data: { ...evt.data, outlookMailResult: successResult } };
        }
        return evt;
      });

      // Append to outlookMailResults array
      const currentResults = message.outlookMailResults || [];

      updateMessageForAgent(agentId, message.id, {
        inlineToolEvents: updatedEvents,
        outlookMailResults: [...currentResults, successResult],
      });
    },
    [
      updateMessageForAgent,
      currentAgent?.id,
      message.id,
      message.inlineToolEvents,
      message.outlookMailResults,
    ]
  );

  // Handler to update message state (for auto-redline applied tracking)
  const handleUpdateMessage = React.useCallback(
    (
      messageId: string,
      updates: { autoRedlineApplied?: boolean; autoRedlineApplyIndex?: number }
    ) => {
      updateMessageForAgent(currentAgent?.id || "unknown", messageId, updates);
    },
    [updateMessageForAgent, currentAgent?.id]
  );

  // Handler for add-comment card status changes (outlookMail pattern).
  // Patches the owning event's status/errorMessage on the message. On a stale
  // block, additionally records a StaleApplyAttempt onto the message so the BE
  // gets the "regenerate against current text" context next turn (via 2.6 →
  // synthesizeStaleApplyAttemptsSummary), mirroring AutoRedlinePreview.
  const handleAddCommentStatusChange = React.useCallback(
    (eventId: string, status: CommentApplyStatus, errorMessage?: string) => {
      const agentId = currentAgent?.id || "unknown";
      const currentEvents = message.inlineToolEvents || [];

      let staleResult: { sectionName: string; paragraphId: number } | undefined;

      const updatedEvents = currentEvents.map((evt: InlineToolEvent) => {
        if (evt.id !== eventId || evt.type !== "addComment" || !evt.data.addComment) {
          return evt;
        }
        if (status === "stale") {
          staleResult = {
            sectionName: evt.data.addComment.result.sectionName,
            paragraphId: evt.data.addComment.result.paragraphId,
          };
        }
        return {
          ...evt,
          data: {
            ...evt.data,
            addComment: { ...evt.data.addComment, status, errorMessage },
          },
        };
      });

      const updates: {
        inlineToolEvents: InlineToolEvent[];
        staleApplyAttempts?: StaleApplyAttempt[];
      } = { inlineToolEvents: updatedEvents };

      // Record the stale attempt (dedupe by sectionName + paragraphId).
      if (staleResult) {
        track("redline:apply_blocked_stale", {
          source: "comment_card",
          paragraph_id: String(staleResult.paragraphId),
        });
        const existing = message.staleApplyAttempts ?? [];
        const already = existing.some(
          (a) =>
            a.sectionName === staleResult!.sectionName && a.paragraphId === staleResult!.paragraphId
        );
        if (!already) {
          updates.staleApplyAttempts = [
            ...existing,
            { sectionName: staleResult.sectionName, paragraphId: staleResult.paragraphId },
          ];
        }
      }

      updateMessageForAgent(agentId, message.id, updates);
    },
    [
      updateMessageForAgent,
      currentAgent?.id,
      message.id,
      message.inlineToolEvents,
      message.staleApplyAttempts,
    ]
  );

  // Log when the component renders - enhanced with thinking information
  React.useEffect(() => {
    log.debug(`Rendering ${isUser ? "user" : "assistant"} message`, {
      messageId: message.id,
      contentLength: typeof message.content === "string" ? message.content.length : "unknown",
      role: message.role,
      isStreaming,
      selectedTextLength: selectedText?.length || 0,
      hasSelectedText: !!selectedText && selectedText.trim().length > 0,
      extendedThinkingEnabled: isExtendedThinkingEnabled(),
    });
  }, [message.id, message.role, isUser, isStreaming, selectedText, isExtendedThinkingEnabled]);

  /**
   * Safely normalize content to string, handling edge cases gracefully
   * This ensures we always have clean string content for rendering
   */
  const normalizeContent = React.useCallback(
    (content: unknown): string => {
      // Debug-level logging for detailed content processing information
      log.debug("Normalizing message content", {
        type: typeof content,
        isArray: Array.isArray(content),
        isNull: content === null,
        isUndefined: content === undefined,
        contentPreview: typeof content === "string" ? content.substring(0, 50) + "..." : content,
      });

      if (typeof content === "string") {
        log.debug("Content is already a string, no transformation needed");
        return content;
      }

      if (Array.isArray(content)) {
        // This is a warning condition - unexpected but recoverable
        log.warn("Message content was unexpectedly an array - joining into string", {
          arrayLength: content.length,
          arrayContents: content,
          messageId: message.id,
        });
        return content.join("");
      }

      if (content == null) {
        log.debug("Content is null or undefined, returning empty string");
        return "";
      }

      // Log when we have to use String() conversion as a fallback
      log.debug("Converting content to string using String() method", {
        originalType: typeof content,
        originalValue: content,
      });

      return String(content);
    },
    [message.id, message.role]
  );

  let messageContent = normalizeContent(message.content);

  // Replace double-check suffix with user-friendly message for fact-checking
  // The fact-check button sends only DOUBLE_CHECK_SUFFIX as the message
  if (isUser && messageContent === DOUBLE_CHECK_SUFFIX) {
    messageContent = "Double checking the fact in your response...";
    log.debug("Replaced double-check suffix with fact-checking indicator", {
      messageId: message.id,
    });
  }

  // NEW: Extract and parse Prompt Coach section from message content (for AI messages only)
  const promptCoachData = React.useMemo(() => {
    if (isUser || !messageContent) {
      return {
        contentBefore: messageContent,
        promptCoachSection: null,
        contentAfter: "",
        parsedData: null,
        headingDetected: false,
      };
    }

    // Extract the section (pass isStreaming to suppress expected warnings)
    const { contentBefore, promptCoachSection, contentAfter, headingDetected } = extractPromptCoach(
      messageContent,
      isStreaming
    );

    // If no heading found, return early (no logging needed - this is expected)
    if (!headingDetected) {
      return {
        contentBefore,
        promptCoachSection: null,
        contentAfter,
        parsedData: null,
        headingDetected: false,
      };
    }

    // Heading detected! If section is empty, still show panel with loading animation
    if (!promptCoachSection) {
      return {
        contentBefore,
        promptCoachSection: null,
        contentAfter,
        parsedData: null,
        headingDetected: true, // Panel will render with loading state
      };
    }

    // Parse the section to validate it (agent-agnostic, pass isStreaming to suppress expected errors)
    const parseResult = parsePromptCoach(promptCoachSection, isStreaming);

    // Even if parsing fails, still show panel with loading animation during streaming
    // Log at INFO level if there was a section but parsing failed (actual issue worth noting)
    if (!parseResult.success) {
      // Only log if NOT streaming (expected to fail during streaming)
      if (!isStreaming) {
        log.info("Prompt Coach section found but parsing incomplete or failed", {
          messageId: message.id,
          errors: parseResult.errors,
        });
      }
      return {
        contentBefore,
        promptCoachSection,
        contentAfter,
        parsedData: null, // Parsing failed but panel will show loading state
        headingDetected: true, // Panel will render with loading state
      };
    }

    return {
      contentBefore,
      promptCoachSection,
      contentAfter,
      parsedData: parseResult.data,
      headingDetected: true, // Panel will render with parsed content
    };
  }, [messageContent, isUser, currentAgent?.id, message.id, isStreaming]);

  // Destructure for use in component
  const { contentBefore, promptCoachSection, contentAfter, parsedData, headingDetected } =
    promptCoachData;

  // NEW: Combined content without Prompt Coach (for copy button and auto-redline positioning)
  const contentWithoutPromptCoach = React.useMemo(() => {
    return promptCoachSection
      ? `${contentBefore}${contentAfter ? "\n\n" + contentAfter : ""}`
      : messageContent;
  }, [contentBefore, contentAfter, promptCoachSection, messageContent]);

  // Display-ready content with incomplete cite tags stripped during streaming.
  // This prevents raw tag text from appearing while LLM outputs citation attributes.
  // Only applied during streaming - complete messages show full content.
  const displayContentBefore = React.useMemo(() => {
    if (!isStreaming) return contentBefore;
    return stripIncompleteCiteTags(contentBefore);
  }, [contentBefore, isStreaming]);

  const displayContentAfter = React.useMemo(() => {
    if (!isStreaming || !contentAfter) return contentAfter;
    return stripIncompleteCiteTags(contentAfter);
  }, [contentAfter, isStreaming]);

  // Log the final processed content for debugging markdown rendering issues
  React.useEffect(() => {
    if (!isUser && messageContent) {
      log.debug("Prepared AI message content for markdown rendering", {
        contentLength: messageContent.length,
        startsWithMarkdown:
          messageContent.startsWith("#") ||
          messageContent.includes("**") ||
          messageContent.includes("*"),
        messageId: message.id,
      });
    }
  }, [messageContent, isUser, message.id]);

  // Only show for user messages that have selected text content
  const hasSelectedText = isUser && selectedText && selectedText.trim().length > 0;

  // Determine if we should show the copy button
  // Only show for assistant messages that are complete (not streaming) and have content
  const shouldShowCopyButton = !isUser && !isStreaming && messageContent.trim().length > 0;

  // Determine if we should show fact-check button
  // Only show for assistant messages that are complete (not streaming), have content, and feature flag is enabled
  const shouldShowFactCheckButton =
    FEATURE_FLAGS.ENABLE_DOUBLE_CHECK &&
    !isUser &&
    !isStreaming &&
    messageContent.trim().length > 0;

  // Determine if we should show feedback buttons
  // Only show for assistant messages that are complete (not streaming) and have content
  const shouldShowFeedbackButtons = !isUser && !isStreaming && messageContent.trim().length > 0;

  // NEW: Determine if we should show tool processing indicator
  const shouldShowToolProcessing = React.useMemo(() => {
    const isAiMessage = !isUser;
    return isAiMessage && isProcessingTool;
  }, [isUser, isProcessingTool]);

  // NEW: Determine if we should show auto-redline outputs
  // Only show for red-line-analyzer agent, AI messages, and when we have outputs
  const shouldShowAutoRedline = React.useMemo(() => {
    const isAiMessage = !isUser;
    const hasAutoRedlines = autoRedlineOutputs && autoRedlineOutputs.length > 0;

    const shouldShow = isAiMessage && hasAutoRedlines;

    // Only log once when we actually show auto-redlines
    if (shouldShow) {
      log.debug("Showing auto-redline outputs", {
        messageId: message.id,
        outputCount: autoRedlineOutputs.length,
      });
    }

    return shouldShow;
  }, [isUser, autoRedlineOutputs, currentAgent?.id, message.id]);

  // NEW: Determine if we should show workflow form
  const shouldShowForm = React.useMemo(() => {
    const isAiMessage = !isUser;
    // Use prop if provided, otherwise fall back to message property
    const payload = formRequestPayload || message.formRequestPayload;
    const hasFormPayload = !!payload;

    const shouldShow = isAiMessage && hasFormPayload;

    if (shouldShow) {
      log.debug("Showing workflow form", {
        messageId: message.id,
        formType: payload?.formType,
        isSubmitted: message.formSubmitted,
      });
    }

    return shouldShow;
  }, [isUser, formRequestPayload, message.formRequestPayload, message.id, message.formSubmitted]);

  // NEW: Determine if we should show playbook viewer
  const shouldShowPlaybook = React.useMemo(() => {
    const isAiMessage = !isUser;
    // Use prop if provided, otherwise fall back to message property
    const result = playbookResult || message.playbookResult;
    const hasPlaybook = !!result;

    const shouldShow = isAiMessage && hasPlaybook;

    if (shouldShow) {
      log.debug("Showing playbook viewer", {
        messageId: message.id,
        reason: result?.reason,
      });
    }

    return shouldShow;
  }, [isUser, playbookResult, message.playbookResult, message.id]);

  // Get the effective playbook result
  const effectivePlaybookResult = playbookResult || message.playbookResult;

  // Get the effective form payload
  const effectiveFormPayload = formRequestPayload || message.formRequestPayload;

  // NEW: Get sorted inline tool events for unified rendering
  // This enables multiple tools to render at their correct positions
  const sortedInlineToolEvents = React.useMemo((): InlineToolEvent[] => {
    const events = message.inlineToolEvents || [];

    // If we have inline events, sort by position and return
    if (events.length > 0) {
      return [...events].sort((a, b) => a.position - b.position);
    }

    // BACKWARD COMPATIBILITY: Synthesize events from legacy single-field approach
    // This ensures existing messages without inlineToolEvents still render correctly
    const synthesizedEvents: InlineToolEvent[] = [];

    // Check for playbook result (legacy)
    if (message.playbookResult && message.playbookPosition !== undefined) {
      synthesizedEvents.push({
        id: `playbook-legacy-${message.id}`,
        type: "playbook",
        position: message.playbookPosition,
        timestamp: message.timestamp,
        data: {
          playbookResult: message.playbookResult,
        },
      });
    }

    // Check for auto-redline outputs (legacy)
    if (
      message.autoRedlineOutputs &&
      message.autoRedlineOutputs.length > 0 &&
      message.autoRedlinePosition !== undefined
    ) {
      synthesizedEvents.push({
        id: `autoRedline-legacy-${message.id}`,
        type: "autoRedline",
        position: message.autoRedlinePosition,
        timestamp: message.timestamp,
        data: {
          autoRedlineOutputs: message.autoRedlineOutputs,
        },
      });
    }

    // Check for Catylex search results (legacy)
    if (
      message.catylexSearchResults &&
      message.catylexSearchResults.length > 0 &&
      message.catylexSearchPosition !== undefined
    ) {
      message.catylexSearchResults.forEach((result, idx) => {
        synthesizedEvents.push({
          id: `catylexSearch-legacy-${message.id}-${idx}`,
          type: "catylexSearch",
          position: message.catylexSearchPosition! + idx,
          timestamp: message.timestamp,
          data: { catylexSearchResult: result },
        });
      });
    }

    // Check for SharePoint search results (legacy)
    if (
      message.sharepointSearchResults &&
      message.sharepointSearchResults.length > 0 &&
      message.sharepointSearchPosition !== undefined
    ) {
      message.sharepointSearchResults.forEach((result, idx) => {
        synthesizedEvents.push({
          id: `sharepointSearch-legacy-${message.id}-${idx}`,
          type: "sharepointSearch",
          position: message.sharepointSearchPosition! + idx,
          timestamp: message.timestamp,
          data: { sharepointSearchResult: result },
        });
      });
    }

    // Check for web search/fetch events (legacy - no inline positioning)
    const hasWebSearchEvents = message.webSearchEvents && message.webSearchEvents.length > 0;
    const hasWebFetchEvents = message.webFetchEvents && message.webFetchEvents.length > 0;
    const hasWebSearchActivity =
      hasWebSearchEvents || hasWebFetchEvents || isSearching || isFetching;
    if (hasWebSearchActivity) {
      synthesizedEvents.push({
        id: `webTools-legacy-${message.id}`,
        type: "webTools",
        position: 0, // Legacy: render at top (preserves current behavior)
        timestamp: message.timestamp,
        data: {
          webSearchEvents: message.webSearchEvents || [],
          webFetchEvents: message.webFetchEvents || [],
          isWebSearching: isSearching,
          isWebFetching: isFetching,
        },
      });
    }

    return synthesizedEvents.sort((a, b) => a.position - b.position);
  }, [
    message.inlineToolEvents,
    message.playbookResult,
    message.playbookPosition,
    message.autoRedlineOutputs,
    message.autoRedlinePosition,
    message.catylexSearchResults,
    message.catylexSearchPosition,
    message.sharepointSearchResults,
    message.sharepointSearchPosition,
    message.webSearchEvents,
    message.webFetchEvents,
    message.id,
    message.timestamp,
    isSearching,
    isFetching,
  ]);

  // Determine if we should use unified inline tool rendering
  const hasInlineToolEvents = sortedInlineToolEvents.length > 0;

  // Handler for form submission success
  const handleFormSubmitSuccess = React.useCallback(
    (successMessage?: string, submittedData?: Record<string, string>) => {
      log.info("Form submitted successfully", {
        messageId: message.id,
        successMessage,
        submittedFieldCount: submittedData ? Object.keys(submittedData).length : 0,
      });

      // Update message to mark form as submitted and store the submitted values
      if (onFormSubmitted) {
        onFormSubmitted(message.id);
      } else {
        // Fallback: update via session context with submitted data
        updateMessageForAgent(currentAgent?.id || "unknown", message.id, {
          formSubmitted: true,
          formSubmittedData: submittedData,
        });
      }
    },
    [message.id, onFormSubmitted, updateMessageForAgent, currentAgent?.id]
  );

  /**
   * Render a single inline tool component based on its type
   */
  const renderInlineToolComponent = React.useCallback(
    (event: InlineToolEvent, index: number) => {
      switch (event.type) {
        case "playbook":
          if (event.data.playbookResult) {
            return <PlaybookViewer key={event.id} result={event.data.playbookResult} />;
          }
          return null;

        case "playbookSelection": {
          const selection = event.data.playbookSelection;
          if (!selection) return null;
          return (
            <PlaybookSelectCard
              key={event.id}
              result={selection.result}
              settled={!!selection.selected}
              selectedName={selection.selectedName}
              disabled={isStreaming}
              onSelect={(option) => onPlaybookSelected?.(message.id, option)}
            />
          );
        }

        case "autoRedline": {
          if (event.data.autoRedlineOutputs && event.data.autoRedlineOutputs.length > 0) {
            const autoRedlineEvents = sortedInlineToolEvents.filter(
              (e) => e.type === "autoRedline"
            );
            const autoApplyIndex = autoRedlineEvents.findIndex((e) => e.id === event.id);

            return (
              <EditPreviewList
                key={event.id}
                autoRedlines={event.data.autoRedlineOutputs}
                composerMode={isComposerModeEnabled()}
                wasComposerModeActiveOnCreation={composerModeActiveOnCreation}
                wasRestoredFromPersistence={message.restoredFromPersistence}
                isStreaming={isStreaming}
                isReadOnly={false}
                messageId={message.id}
                onUpdateMessage={handleUpdateMessage}
                hasAlreadyApplied={message.autoRedlineApplied}
                autoApplyIndex={autoApplyIndex}
                totalAutoApplyLists={autoRedlineEvents.length}
                currentApplyTurn={message.autoRedlineApplyIndex}
              />
            );
          }
          return null;
        }

        case "addComment": {
          if (event.data.addComment) {
            return (
              <AddCommentCard
                key={event.id}
                event={event}
                onStatusChange={handleAddCommentStatusChange}
              />
            );
          }
          return null;
        }

        case "webTools": {
          // Convert inline event data to WebToolEvent[] format for WebToolsSection
          const searchToolEvents: WebToolEvent[] = (event.data.webSearchEvents || []).map(
            (e): WebSearchToolEvent => ({
              ...e,
              toolType: "web_search",
            })
          );
          const fetchToolEvents: WebToolEvent[] = (event.data.webFetchEvents || []).map(
            (e): WebFetchToolEvent => ({
              ...e,
              toolType: "web_fetch",
            })
          );
          // Sort by order number (searchNumber or fetchNumber)
          const getOrder = (e: WebToolEvent): number =>
            e.toolType === "web_search" ? e.searchNumber : e.fetchNumber;
          const inlineWebToolEvents = [...searchToolEvents, ...fetchToolEvents].sort(
            (a, b) => getOrder(a) - getOrder(b)
          );

          const hasData =
            inlineWebToolEvents.length > 0 || event.data.isWebSearching || event.data.isWebFetching;

          if (!hasData) return null;

          return (
            <div key={event.id} style={{ width: "100%" }}>
              <WebToolsSection
                toolEvents={inlineWebToolEvents}
                isSearching={event.data.isWebSearching || false}
                isFetching={event.data.isWebFetching || false}
                isBenchmarkMode={FEATURE_FLAGS.ENABLE_BENCHMARK_MODE && settings.benchmarkMode}
              />
            </div>
          );
        }

        case "catylexSearch": {
          if (event.data.catylexSearchResult) {
            return <CatylexSearchSection key={event.id} result={event.data.catylexSearchResult} />;
          }
          return null;
        }

        case "sharepointSearch": {
          if (event.data.sharepointSearchResult) {
            return (
              <SharePointSearchSection key={event.id} result={event.data.sharepointSearchResult} />
            );
          }
          return null;
        }

        case "outlookMail": {
          if (event.data.outlookMailResult) {
            return (
              <OutlookMailSection
                key={event.id}
                result={event.data.outlookMailResult}
                onSendComplete={(successResult) =>
                  handleOutlookMailSendComplete(event.id, successResult)
                }
                onSendPrompt={onFactCheck}
                isStreaming={isStreaming}
              />
            );
          }
          return null;
        }

        case "thinking": {
          const thinking = event.data.thinking;
          if (!thinking) return null;
          return (
            <div key={event.id} style={{ width: "100%" }}>
              <ThinkingSection content={thinking.content} isStreaming={thinking.isStreaming} />
            </div>
          );
        }

        default:
          return null;
      }
    },
    [
      isStreaming,
      isComposerModeEnabled,
      composerModeActiveOnCreation,
      message.id,
      message.autoRedlineApplied,
      message.autoRedlineApplyIndex,
      handleUpdateMessage,
      handleOutlookMailSendComplete,
      handleAddCommentStatusChange,
      sortedInlineToolEvents,
      settings.benchmarkMode,
      onPlaybookSelected,
      onFactCheck,
    ]
  );

  /**
   * Render content with inline tool events interleaved at their positions
   * This is the unified rendering approach that enables multiple tools to appear
   */
  const renderContentWithInlineTools = React.useCallback(() => {
    if (sortedInlineToolEvents.length === 0) {
      // No inline tools - render content normally
      return (
        <>
          <MarkdownRenderer>{contentBefore}</MarkdownRenderer>
          {contentAfter && <MarkdownRenderer>{contentAfter}</MarkdownRenderer>}
        </>
      );
    }

    const elements: React.ReactNode[] = [];
    let lastPosition = 0;

    sortedInlineToolEvents.forEach((event, index) => {
      // Render content segment before this tool
      if (event.position > lastPosition) {
        const contentSegment = contentWithoutPromptCoach.substring(lastPosition, event.position);
        // Apply cite tag stripping during streaming to prevent raw tags from showing
        const displaySegment = isStreaming
          ? stripIncompleteCiteTags(contentSegment)
          : contentSegment;
        if (displaySegment.length > 0) {
          elements.push(
            <MarkdownRenderer key={`content-before-${event.id}`}>{displaySegment}</MarkdownRenderer>
          );
        }
      }

      // Render the tool component
      const toolComponent = renderInlineToolComponent(event, index);
      if (toolComponent) {
        elements.push(toolComponent);
      }

      lastPosition = event.position;
    });

    // Render remaining content after the last tool
    if (lastPosition < contentWithoutPromptCoach.length) {
      const remainingContent = contentWithoutPromptCoach.substring(lastPosition);
      // Apply cite tag stripping during streaming to prevent raw tags from showing
      const displayRemaining = isStreaming
        ? stripIncompleteCiteTags(remainingContent)
        : remainingContent;
      if (displayRemaining.length > 0) {
        elements.push(
          <MarkdownRenderer key="content-after-all-tools">{displayRemaining}</MarkdownRenderer>
        );
      }
    }

    return <>{elements}</>;
  }, [
    sortedInlineToolEvents,
    contentWithoutPromptCoach,
    contentBefore,
    contentAfter,
    renderInlineToolComponent,
    isStreaming,
  ]);

  // Log selected text indicator decision for debugging
  React.useEffect(() => {
    if (isUser && selectedText) {
      log.debug("Selected text indicator decision", {
        messageId: message.id,
        hasSelectedText,
        selectedTextLength: selectedText.length,
        selectedTextPreview: selectedText.substring(0, 50) + "...",
        willRenderIndicator: hasSelectedText,
      });
    }
  }, [hasSelectedText, message.id, isUser, selectedText]);

  // Log copy button visibility decision for debugging
  React.useEffect(() => {
    if (!isUser) {
      log.debug("Copy button visibility decision", {
        messageId: message.id,
        shouldShow: shouldShowCopyButton,
        isStreaming,
        hasContent: messageContent.trim().length > 0,
        contentLength: messageContent.length,
      });
    }
  }, [shouldShowCopyButton, isStreaming, messageContent, message.id, isUser]);

  // Self-contained styles - no external dependencies
  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column", // Changed to column to stack message and copy button
    width: "100%",
    marginBottom: "16px", // Space between messages
    alignItems: isUser ? "flex-end" : "flex-start", // User messages on right, AI on left
    fontFamily: "'Segoe UI', sans-serif",
  };

  // Alignment override wrapper for SelectedTextIndicator
  // This forces the selected text indicator to appear on the left side
  // while preserving its internal right-aligned attribution
  const selectedTextWrapperStyle: React.CSSProperties = {
    alignSelf: "flex-start", // Override parent's flex-end alignment for user messages
    width: "100%", // Take full width so the internal right-alignment has space to work
    marginBottom: "8px", // Visual separation between indicator and message bubble
  };

  const messageContentStyle: React.CSSProperties = isUser
    ? {
        // User message: visible bubble with light gray background
        backgroundColor: "#F2F2F2",
        color: "#333333",
        padding: "12px 16px",
        borderRadius: "18px", // Rounded bubble appearance
        maxWidth: "75%", // Prevent overly wide messages
        wordWrap: "break-word",
        fontSize: "14px",
        lineHeight: "20px",
      }
    : {
        // AI message: appears directly on background, no visible bubble
        backgroundColor: "transparent", // No background - appears on main background
        color: "#333333",
        width: "100%", // Fixed width for stable layout during streaming
        maxWidth: "95%", // Wider for AI responses since they're often longer
        wordWrap: "break-word",
        fontSize: "14px",
        lineHeight: "20px",
        // No padding or border radius - content appears directly on background
      };

  // Styles for the action buttons container (copy and feedback)
  const actionButtonsContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between", // Separate copy (left) and feedback (right) buttons
    alignItems: "center",
    width: "100%", // Full width to enable space-between
    marginTop: "6px",
  };

  return (
    <div style={containerStyle}>
      {/* Wrapper with alignment override for selected text indicator */}
      {hasSelectedText && (
        <div style={selectedTextWrapperStyle}>
          <SelectedTextIndicator selectedText={selectedText} compact={false} />
        </div>
      )}

      {/* Message content - with unified inline tool rendering for AI messages */}
      <div style={messageContentStyle}>
        {isUser ? (
          // User messages: simple text rendering (no Prompt Coach extraction).
          // A message an add-in action sent shows the action the user took
          // rather than the text written for the model; the text is still
          // sent and still sits in the conversation history.
          message.action === "quick_analysis" ? (
            <QuickAnalysisIndicator />
          ) : message.action === "draft_certificate" ? (
            <DraftCertificateIndicator />
          ) : messageContent.startsWith(BEAUTIFY_EMAIL_PREFIX) ? (
            <BeautifyIndicator />
          ) : (
            messageContent
          )
        ) : (
          // AI messages: wrap with WebSearchResultsProvider for citation title lookup
          // and DocumentCitationProvider for document citation data lookup
          <WebSearchResultsProvider
            webSearchEvents={webSearchEvents}
            webFetchEvents={message.webFetchEvents || []}
          >
            <DocumentCitationProvider citations={message.documentCitations || EMPTY_CITATIONS}>
              {hasInlineToolEvents ? (
                // AI messages with inline tool events: use unified rendering approach
                // This handles multiple tools at their correct positions (playbook, auto-redline, etc.)
                renderContentWithInlineTools()
              ) : (
                // AI messages without inline tools: render content before and after separately
                // Use display versions to hide incomplete cite tags during streaming
                <>
                  <MarkdownRenderer>{displayContentBefore}</MarkdownRenderer>
                  {displayContentAfter && (
                    <MarkdownRenderer>{displayContentAfter}</MarkdownRenderer>
                  )}
                </>
              )}
            </DocumentCitationProvider>
          </WebSearchResultsProvider>
        )}
      </div>

      {/* Tool Processing Section - rendered AFTER content as transient indicator */}
      {/* This shows at the end during tool execution, NOT inline like completed tools */}
      {shouldShowToolProcessing && (
        <div style={{ width: "100%", maxWidth: "95%" }}>
          {message.processingToolName === "catylex_search" ? (
            <CatylexProcessingSection
              isProcessingTool={isProcessingTool}
              startTime={message.toolProcessingStartTime}
              toolInput={message.catylexToolInput}
            />
          ) : message.processingToolName === "sharepoint_search" ? (
            <SharePointProcessingSection
              isProcessingTool={isProcessingTool}
              startTime={message.toolProcessingStartTime}
              toolInput={message.sharepointToolInput}
            />
          ) : message.processingToolName === "outlook_mail" ? (
            <OutlookMailProcessing
              isProcessingTool={isProcessingTool}
              startTime={message.toolProcessingStartTime}
            />
          ) : message.processingToolName === "add_comment_tool" ? (
            <ToolProcessingSection
              isProcessingTool={isProcessingTool}
              messages={ADD_COMMENT_PROCESSING_MESSAGES}
            />
          ) : (
            <ToolProcessingSection isProcessingTool={isProcessingTool} />
          )}
        </div>
      )}

      {/* NEW: Prompt Coach Panel (rendered separately after main content) */}
      {/* Render as soon as heading is detected (even if parsing incomplete during streaming) */}
      {!isUser && headingDetected && onPromptClick && (
        <PromptCoachPanel
          parsedData={parsedData}
          onPromptClick={onPromptClick}
          isStreaming={isStreaming}
          pinnedAction={
            onDraftCertificate ? <DraftCertificateCard onClick={onDraftCertificate} /> : undefined
          }
        />
      )}

      {/* The Draft Certificate offer where there is no Prompt Coach to carry it.
          The certificate answer itself ends without one, and the offer has to
          outlive that for the certificate to be re-run after changes. */}
      {!isUser && !headingDetected && onDraftCertificate && (
        <div style={{ width: "100%", maxWidth: "95%", marginBottom: "16px" }}>
          <DraftCertificateCard onClick={onDraftCertificate} />
        </div>
      )}

      {/* NEW: Workflow Form (rendered after main content) */}
      {shouldShowForm && effectiveFormPayload && (
        <div style={{ width: "100%", maxWidth: "95%" }}>
          <DynamicForm
            payload={effectiveFormPayload}
            onSubmitSuccess={handleFormSubmitSuccess}
            isReadOnly={message.formSubmitted}
          />
        </div>
      )}

      {/* PlaybookViewer is now rendered INLINE within message content (see playbook condition above) */}
      {/* This ensures proper content flow: text before → playbook card → text after */}

      {/* Action buttons for completed AI messages (copy, fact-check, and feedback) */}
      {(shouldShowCopyButton || shouldShowFactCheckButton || shouldShowFeedbackButtons) && (
        <div style={actionButtonsContainerStyle}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            {/* Left side - Copy and Fact-check buttons */}
            {shouldShowCopyButton && (
              <CopyButton textToCopy={contentWithoutPromptCoach} className="message-copy-button" />
            )}
            {shouldShowFactCheckButton && (
              <FactCheckButton
                messageContent={contentWithoutPromptCoach}
                onFactCheck={onFactCheck}
                className="message-fact-check-button"
              />
            )}
            {shouldShowCopyButton && isDraftCertificateAnswer && (
              <ExportToWordButton markdown={contentWithoutPromptCoach} />
            )}
          </div>
          <div>
            {/* Right side - Feedback buttons */}
            {shouldShowFeedbackButtons && (
              <FeedbackButtons
                messageId={message.id}
                sessionId={feedbackSessionId}
                // docId omitted - no proper document identifier available
                agentId={currentAgent?.id}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
